import { useEffect, useState } from "react";
import { navCategories, hotRank, latestNews, heroSideItems, breakingStory } from "@/mocks/homeNews";

const searchPool = [
  ...hotRank.map((i) => i.title),
  ...latestNews.map((i) => i.title),
  ...heroSideItems.map((i) => i.title),
  breakingStory.title,
];

interface HeaderProps {
  query: string;
  setQuery: (v: string) => void;
  onPick: () => void;
}

export default function Header({ query, setQuery, onPick }: HeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const matches = focus && query.trim().length > 0
    ? searchPool.filter((t) => t.includes(query.trim())).slice(0, 6)
    : [];

  return (
    <header
      className={
        "sticky top-0 z-50 w-full transition-all duration-300 " +
        (scrolled
          ? "border-b border-background-200/70 bg-background-50/92 shadow-[0_1px_0_0] shadow-background-200/40 backdrop-blur"
          : "border-b border-transparent bg-background-50")
      }
    >
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="flex h-16 items-center justify-between gap-4">
          {/* brand */}
          <a href="#top" className="flex shrink-0 items-center gap-2" aria-label="71吃瓜 首页">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary-500 text-lg font-bold text-background-50 dark:text-foreground-950">
              71
            </span>
            <span className="hidden flex-col leading-none sm:flex">
              <span className="font-heading text-lg font-black tracking-wide text-foreground-950">71吃瓜</span>
              <span className="mt-0.5 text-[11px] text-foreground-500">新瓜随手翻 · 拿起接着看</span>
            </span>
          </a>

          {/* desktop nav */}
          <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
            {navCategories.map((c) => (
              <a
                key={c}
                href={c === "今日速报" ? "#today" : `#cat-${c}`}
                className="whitespace-nowrap rounded-full px-3 py-2 text-sm text-foreground-600 transition hover:bg-secondary-100 hover:text-foreground-950"
              >
                {c}
              </a>
            ))}
          </nav>

          {/* search */}
          <div className="relative hidden flex-1 max-w-xs md:block">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocus(true)}
                onBlur={() => setTimeout(() => setFocus(false), 150)}
                placeholder="搜瓜、搜剧、搜梗"
                className="w-full rounded-full border border-background-300/70 bg-background-100 py-2 pl-9 pr-3 text-sm text-foreground-950 outline-none transition placeholder:text-foreground-400 focus:border-primary-400 focus:bg-background-50 focus:ring-2 focus:ring-primary-200"
              />
            </div>
            {matches.length > 0 && (
              <div className="absolute left-0 right-0 top-11 z-40 overflow-hidden rounded-xl border border-background-200 bg-background-50 py-1 shadow-lg shadow-background-300/30">
                {matches.map((m, idx) => (
                  <a
                    key={idx}
                    href="#today"
                    onClick={() => {
                      setQuery("");
                      onPick();
                    }}
                    className="flex items-start gap-2 px-3 py-2 text-sm text-foreground-700 transition hover:bg-secondary-100"
                  >
                    <i className="ri-chat-search-line mt-0.5 text-primary-500" />
                    <span className="line-clamp-1">{m}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <a
              href="#today"
              className="hidden whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50 transition hover:bg-primary-600 md:inline-flex dark:text-foreground-950"
            >
              今日速报
            </a>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full bg-secondary-100 text-lg text-foreground-800 transition hover:bg-secondary-200 lg:hidden"
              aria-label="打开菜单"
            >
              <i className={open ? "ri-close-line" : "ri-menu-line"} />
            </button>
          </div>
        </div>
      </div>

      {/* mobile panel */}
      {open && (
        <div className="border-t border-background-200 bg-background-50 px-4 pb-4 pt-3 lg:hidden">
          <div className="mb-3 md:hidden">
            <div className="relative">
              <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-foreground-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="搜瓜、搜剧、搜梗"
                className="w-full rounded-full border border-background-300/70 bg-background-100 py-2 pl-9 pr-3 text-sm outline-none placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {navCategories.map((c) => (
              <a
                key={c}
                href={c === "今日速报" ? "#today" : `#cat-${c}`}
                onClick={() => setOpen(false)}
                className="rounded-xl bg-secondary-100 px-3 py-2.5 text-center text-sm text-foreground-700 transition hover:bg-secondary-200"
              >
                {c}
              </a>
            ))}
          </div>
          <a
            href="#chat"
            onClick={() => setOpen(false)}
            className="mt-3 flex items-center justify-center gap-2 rounded-full bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 dark:text-foreground-950"
          >
            <i className="ri-chat-heart-line" /> 去瓜田热聊
          </a>
        </div>
      )}
    </header>
  );
}